import { Loader2, ShieldCheck, ShieldHalf } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import type {
  Equipment,
  WarrantySource,
} from '@/features/equipment/types';
import { daysFromToday } from '@/features/warranty/format';
import {
  useEquipmentWarrantyHistory,
  useExtendEquipmentWarranty,
  useOverrideEquipmentWarranty,
  useResetEquipmentWarranty,
} from '@/features/warranty/queries';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

interface EquipmentWarrantyCardProps {
  equipment: Equipment;
}

function SourceBadge({ source }: { source: WarrantySource }) {
  const { t } = useTranslation();
  const tone =
    source === 'INHERITED'
      ? 'bg-blue-100 text-blue-700'
      : source === 'ORIGINAL'
        ? 'bg-amber-100 text-amber-800'
        : 'bg-emerald-100 text-emerald-700';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
        tone,
      )}
    >
      {t(`warranty.source.${source}`)}
    </span>
  );
}

export function EquipmentWarrantyCard({ equipment }: EquipmentWarrantyCardProps) {
  const { t } = useTranslation();
  const overrideMut = useOverrideEquipmentWarranty(
    equipment.id,
    equipment.vesselId,
  );
  const resetMut = useResetEquipmentWarranty(equipment.id, equipment.vesselId);
  const extendMut = useExtendEquipmentWarranty(equipment.id, equipment.vesselId);
  const { data: history, isLoading: isHistoryLoading } =
    useEquipmentWarrantyHistory(equipment.id);

  const [overrideOpen, setOverrideOpen] = useState(false);
  const [extendOpen, setExtendOpen] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  // Override modal state
  const [overrideEnd, setOverrideEnd] = useState<string>('');
  const [overrideStart, setOverrideStart] = useState<string>('');
  const [overrideReason, setOverrideReason] = useState<string>('');
  const [overrideErrorKey, setOverrideErrorKey] = useState<string | null>(null);

  // Extend modal state
  const [extendMonths, setExtendMonths] = useState<string>('12');
  const [extendReason, setExtendReason] = useState<string>('');
  const [extendErrorKey, setExtendErrorKey] = useState<string | null>(null);

  const endDate = equipment.warrantyEndDate;
  const daysLeft = daysFromToday(endDate);

  const handleOverrideSubmit = () => {
    setOverrideErrorKey(null);
    if (!overrideEnd) return;
    overrideMut.mutate(
      {
        warrantyEndDate: new Date(overrideEnd).toISOString(),
        warrantyStartDate: overrideStart
          ? new Date(overrideStart).toISOString()
          : undefined,
        reason: overrideReason.trim() || undefined,
      },
      {
        onSuccess: () => {
          setOverrideOpen(false);
          setOverrideEnd('');
          setOverrideStart('');
          setOverrideReason('');
        },
        onError: (err) => {
          setOverrideErrorKey(
            err instanceof ApiError ? err.key : 'warranty.override.saveError',
          );
        },
      },
    );
  };

  const handleExtendSubmit = () => {
    setExtendErrorKey(null);
    const months = parseInt(extendMonths, 10);
    if (!Number.isFinite(months) || months <= 0) return;
    if (!extendReason.trim()) return;
    extendMut.mutate(
      { durationMonths: months, reason: extendReason.trim() },
      {
        onSuccess: () => {
          setExtendOpen(false);
          setExtendMonths('12');
          setExtendReason('');
        },
        onError: (err) => {
          setExtendErrorKey(
            err instanceof ApiError ? err.key : 'warranty.extend.saveError',
          );
        },
      },
    );
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck aria-hidden className="h-4 w-4" />
            {t('warranty.title')}
          </CardTitle>
          <SourceBadge source={equipment.warrantySource} />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            {endDate ? (
              <>
                <p className="text-sm">
                  {t('warranty.endDate')}:{' '}
                  <span className="font-medium">
                    {new Date(endDate).toLocaleDateString()}
                  </span>
                </p>
                {daysLeft !== null && (
                  <p
                    className={cn(
                      'text-xs',
                      daysLeft < 0
                        ? 'text-red-700'
                        : daysLeft < 90
                          ? 'text-amber-700'
                          : 'text-emerald-700',
                    )}
                  >
                    {daysLeft >= 0
                      ? t('warranty.expiresIn', { count: daysLeft })
                      : t('warranty.expired', { count: Math.abs(daysLeft) })}
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('warranty.noEndDate')}
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {equipment.warrantySource === 'INHERITED' ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOverrideOpen(true)}
              >
                <ShieldHalf aria-hidden className="h-4 w-4" />
                {t('warranty.override.action')}
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setResetConfirmOpen(true)}
                disabled={resetMut.isPending}
              >
                {resetMut.isPending && (
                  <Loader2 className="animate-spin" aria-hidden />
                )}
                {t('warranty.reset.action')}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExtendOpen(true)}
            >
              {t('warranty.extend.action')}
            </Button>
          </div>

          <div className="space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('warranty.history.title')}
            </h4>
            {isHistoryLoading ? (
              <Skeleton className="h-12 w-full" />
            ) : (history ?? []).length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {t('warranty.history.empty')}
              </p>
            ) : (
              <ul className="space-y-2">
                {(history ?? []).map((ext) => (
                  <li
                    key={ext.id}
                    className="rounded-md border p-2 text-xs"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">
                        {t('warranty.history.monthsAdded', {
                          count: ext.durationMonths,
                        })}
                      </span>
                      <span className="text-muted-foreground">
                        →{' '}
                        {new Date(ext.newEndDate).toLocaleDateString()}
                      </span>
                      <span className="ml-auto text-muted-foreground">
                        {new Date(ext.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="mt-1 text-muted-foreground">{ext.reason}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Override modal */}
      <Dialog
        open={overrideOpen}
        onOpenChange={(o) => {
          if (!o) {
            setOverrideOpen(false);
            setOverrideErrorKey(null);
            overrideMut.reset();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('warranty.override.title')}</DialogTitle>
            <DialogDescription>
              {t('warranty.override.description')}
            </DialogDescription>
          </DialogHeader>
          {overrideErrorKey && (
            <Alert variant="destructive">
              <AlertTitle>{t('warranty.override.saveError')}</AlertTitle>
              <AlertDescription>
                {t(overrideErrorKey, {
                  defaultValue: t('warranty.override.saveError'),
                })}
              </AlertDescription>
            </Alert>
          )}
          <div className="space-y-3">
            <div className="space-y-1">
              <label htmlFor="warranty-override-end" className="text-sm font-medium">
                {t('warranty.override.endDateLabel')}
              </label>
              <Input
                id="warranty-override-end"
                type="date"
                value={overrideEnd}
                onChange={(e) => setOverrideEnd(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="warranty-override-start" className="text-sm font-medium">
                {t('warranty.override.startDateLabel')}
              </label>
              <Input
                id="warranty-override-start"
                type="date"
                value={overrideStart}
                onChange={(e) => setOverrideStart(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="warranty-override-reason" className="text-sm font-medium">
                {t('warranty.override.reasonLabel')}
              </label>
              <Input
                id="warranty-override-reason"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setOverrideOpen(false)}
              disabled={overrideMut.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleOverrideSubmit}
              disabled={!overrideEnd || overrideMut.isPending}
            >
              {overrideMut.isPending && (
                <Loader2 className="animate-spin" aria-hidden />
              )}
              {t('warranty.override.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend modal */}
      <Dialog
        open={extendOpen}
        onOpenChange={(o) => {
          if (!o) {
            setExtendOpen(false);
            setExtendErrorKey(null);
            extendMut.reset();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('warranty.extend.title')}</DialogTitle>
            <DialogDescription>
              {t('warranty.extend.description')}
            </DialogDescription>
          </DialogHeader>
          {extendErrorKey && (
            <Alert variant="destructive">
              <AlertTitle>{t('warranty.extend.saveError')}</AlertTitle>
              <AlertDescription>
                {t(extendErrorKey, {
                  defaultValue: t('warranty.extend.saveError'),
                })}
              </AlertDescription>
            </Alert>
          )}
          <div className="space-y-3">
            <div className="space-y-1">
              <label htmlFor="warranty-extend-duration" className="text-sm font-medium">
                {t('warranty.extend.durationLabel')}
              </label>
              <Input
                id="warranty-extend-duration"
                type="number"
                min="1"
                max="120"
                value={extendMonths}
                onChange={(e) => setExtendMonths(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="warranty-extend-reason" className="text-sm font-medium">
                {t('warranty.extend.reasonLabel')}
              </label>
              <Input
                id="warranty-extend-reason"
                value={extendReason}
                onChange={(e) => setExtendReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setExtendOpen(false)}
              disabled={extendMut.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleExtendSubmit}
              disabled={!extendReason.trim() || extendMut.isPending}
            >
              {extendMut.isPending && (
                <Loader2 className="animate-spin" aria-hidden />
              )}
              {t('warranty.extend.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={resetConfirmOpen}
        onOpenChange={setResetConfirmOpen}
        title={t('warranty.reset.title')}
        description={t('warranty.reset.description')}
        confirmLabel={t('warranty.reset.submit')}
        variant="destructive"
        loading={resetMut.isPending}
        onConfirm={() => {
          resetMut.mutate(undefined, {
            onSuccess: () => setResetConfirmOpen(false),
          });
        }}
      />
    </>
  );
}
